import React, { useState, useEffect, useMemo, useCallback } from 'react';
import Shell from './tuteur/Shell';

// ── Compréhension orale (page PERSONNELLE, propriétaire uniquement) ──────────
// Accès via ?ecoute. Le contenu vient de /api/listening, réservé aux `user_id`
// inscrits dans la table `owners` : 401 si non connecté, 403 sinon.
//
// DISCRÉTION : qui n'y a pas droit est renvoyé à l'accueil, sans le moindre écran
// d'explication. Ni « créez un compte » (ce serait inviter à s'inscrire pour un
// contenu inaccessible), ni « espace privé » (ce serait révéler qu'il existe) :
// pour tout le monde sauf le propriétaire, `?ecoute` se comporte comme un
// paramètre inconnu.
//
// MULTILINGUE : la langue vient du DOMAINE (lang.apiLang). Titres, questions et
// choix sont des dicts par langue côté API ; l'UI a son propre dictionnaire STR.
// Le texte russe (ce qu'on ENTEND) est le même pour toutes les langues.
//
// Parti pris pédagogique : c'est de l'écoute, PAS de la lecture. Le texte russe
// reste MASQUÉ ; on écoute, on répond aux trois questions (facile → difficile),
// et la transcription ne se dévoile qu'ensuite. Les propositions sont mélangées
// côté client (la bonne réponse est `choices[<lang>][0]` côté serveur).

const SERIF = "Georgia, 'Times New Roman', 'Noto Serif', serif";
const TOKEN_KEY = 'motrusse_token';
const LEVELS = ['A1', 'A2', 'B1', 'B2'];

// Chaînes d'interface par langue. `tiers` est clé sur la valeur `tier` stockée
// (indépendante de la langue). `register` sur la valeur stockée ('familier').
const STR = {
  fr: {
    title: 'Compréhension orale',
    subtitle: 'De courts textes russes à écouter, puis trois questions par texte, du plus simple au plus fin.',
    listenPrompt: 'Écoutez, puis répondez. Réécoutez autant que nécessaire.',
    showText: 'Voir le texte', hideText: 'Masquer le texte',
    all: 'Tous', listenAria: 'Écouter',
    tiers: { facile: 'Facile', moyen: 'Moyen', difficile: 'Difficile' },
    register: { familier: 'familier', recit: 'récit', monde: 'monde' },
    errorMsg: 'Impossible de charger les textes.', retry: 'Réessayer',
    empty: 'Aucun texte pour ce niveau.',
    done: 'déjà travaillé',
  },
  en: {
    title: 'Listening comprehension',
    subtitle: 'Short Russian texts to listen to, then three questions each, from the simplest to the subtlest.',
    listenPrompt: 'Listen, then answer. Replay as often as you like.',
    showText: 'Show text', hideText: 'Hide text',
    all: 'All', listenAria: 'Listen',
    tiers: { facile: 'Easy', moyen: 'Medium', difficile: 'Hard' },
    register: { familier: 'informal', recit: 'story', monde: 'world' },
    errorMsg: "Couldn't load the texts.", retry: 'Try again',
    empty: 'No texts for this level.',
    done: 'done before',
  },
  de: {
    title: 'Hörverstehen',
    subtitle: 'Kurze russische Texte zum Anhören, dann drei Fragen pro Text, von der einfachsten bis zur feinsten.',
    listenPrompt: 'Hören Sie zu, dann antworten Sie. Beliebig oft wiederholbar.',
    showText: 'Text anzeigen', hideText: 'Text ausblenden',
    all: 'Alle', listenAria: 'Anhören',
    tiers: { facile: 'Leicht', moyen: 'Mittel', difficile: 'Schwer' },
    register: { familier: 'umgangssprachlich', recit: 'Geschichte', monde: 'Welt' },
    errorMsg: 'Texte konnten nicht geladen werden.', retry: 'Erneut versuchen',
    empty: 'Keine Texte für dieses Niveau.',
    done: 'schon geübt',
  },
  es: {
    title: 'Comprensión oral',
    subtitle: 'Textos rusos breves para escuchar, luego tres preguntas por texto, de la más simple a la más sutil.',
    listenPrompt: 'Escucha y luego responde. Vuelve a escuchar cuantas veces quieras.',
    showText: 'Ver el texto', hideText: 'Ocultar el texto',
    all: 'Todos', listenAria: 'Escuchar',
    tiers: { facile: 'Fácil', moyen: 'Medio', difficile: 'Difícil' },
    register: { familier: 'coloquial', recit: 'relato', monde: 'mundo' },
    errorMsg: 'No se pudieron cargar los textos.', retry: 'Reintentar',
    empty: 'No hay textos para este nivel.',
    done: 'ya practicado',
  },
  it: {
    title: 'Comprensione orale',
    subtitle: 'Brevi testi russi da ascoltare, poi tre domande per testo, dalla più semplice alla più sottile.',
    listenPrompt: 'Ascolta, poi rispondi. Riascolta quante volte vuoi.',
    showText: 'Mostra il testo', hideText: 'Nascondi il testo',
    all: 'Tutti', listenAria: 'Ascolta',
    tiers: { facile: 'Facile', moyen: 'Medio', difficile: 'Difficile' },
    register: { familier: 'colloquiale', recit: 'racconto', monde: 'mondo' },
    errorMsg: 'Impossibile caricare i testi.', retry: 'Riprova',
    empty: 'Nessun testo per questo livello.',
    done: 'già fatto',
  },
};

// L'audio est servi en `Cache-Control: public, immutable` : sans empreinte dans
// l'URL, un mp3 regénéré sous le même nom resterait l'ancien dans le navigateur
// pendant un mois (aucune revalidation). D'où le ?v= tiré du hash du contenu.
const audioSrc = (ex) =>
  `/static/${ex.audio}${ex.audio_hash ? `?v=${ex.audio_hash.slice(0, 8)}` : ''}`;

// Résout un champ multilingue (dict {fr,en,…}) vers la langue courante, avec repli
// fr puis 1re valeur. Tolère une chaîne/tableau nu (ancien format) inchangé.
const pick = (v, lang) =>
  (v && typeof v === 'object' && !Array.isArray(v)) ? (v[lang] ?? v.fr ?? Object.values(v)[0]) : v;

function shuffle(arr) {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

// Prépare une fois les propositions mélangées ET résolues dans la langue courante
// (stable tant que data/lang ne changent pas) : sans ça, chaque frappe re-mélangerait.
function prepare(exercises, lang) {
  return exercises.map((ex) => ({
    slug: ex.slug,
    level: ex.level,
    register: ex.register,
    text_ru: ex.text_ru,
    audio: ex.audio, // NE PAS oublier : audioSrc() en dépend (sinon /static/undefined)
    audio_hash: ex.audio_hash, // idem : sert de casse-cache dans l'URL
    title: pick(ex.title, lang),
    questions: (ex.questions || []).map((q) => ({
      tier: q.tier,
      q: pick(q.q, lang),
      options: shuffle((pick(q.choices, lang) || []).map((label, i) => ({ label, correct: i === q.answer }))),
    })),
  }));
}


const Speaker = ({ onClick, big, label }) => (
  <button
    onClick={onClick}
    title={label}
    aria-label={label}
    className={`shrink-0 inline-flex items-center justify-center rounded-full bg-blue-600 hover:bg-blue-500 active:scale-95 transition-all ${big ? 'w-16 h-16' : 'w-10 h-10'}`}
  >
    <svg viewBox="0 0 24 24" className={`fill-white ${big ? 'w-7 h-7' : 'w-4 h-4'}`}>
      <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02z" />
    </svg>
  </button>
);

const LevelPill = ({ level }) => (
  <span className="shrink-0 text-[11px] font-bold tracking-wide px-2 py-0.5 rounded-full bg-blue-500/15 text-blue-300">
    {level}
  </span>
);

// ── Une question à choix multiple ────────────────────────────────────────────
const Question = ({ question, chosen, onChoose, tierLabel }) => {
  const answered = chosen != null;
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">{tierLabel}</span>
        <span className="h-px flex-1 bg-slate-700/60" />
      </div>
      <p className="text-sm md:text-base text-slate-100 font-medium">{question.q}</p>
      <div className="grid grid-cols-1 gap-2">
        {question.options.map((opt, i) => {
          let cls = 'bg-slate-700 text-white border-slate-600 hover:bg-blue-600 hover:border-blue-500';
          if (answered) {
            if (opt.correct) cls = 'bg-emerald-600/25 text-emerald-200 border-emerald-500/60';
            else if (opt === chosen) cls = 'bg-rose-600/20 text-rose-200 border-rose-500/50';
            else cls = 'bg-slate-800 text-slate-500 border-slate-700';
          }
          return (
            <button
              key={i}
              disabled={answered}
              onClick={() => onChoose(opt)}
              className={`text-left px-4 py-3 rounded-xl text-sm border transition-all active:scale-[0.99] disabled:active:scale-100 ${cls}`}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );
};

// ── Une carte-exercice (accordéon) ───────────────────────────────────────────
const ExerciseCard = ({ ex, open, onToggle, play, answers, onAnswer, prog, S }) => {
  const [showText, setShowText] = useState(false);
  const chosen = answers[ex.slug] || {};
  const answeredCount = Object.keys(chosen).length;
  const total = ex.questions.length;
  const done = answeredCount === total;
  const score = ex.questions.reduce((s, q, i) => s + (chosen[i]?.correct ? 1 : 0), 0);
  const registerLabel = S.register[ex.register];
  // Progression PERSISTÉE (BDD) : affichée seulement tant qu'on n'a pas rejoué ce
  // texte dans la session courante (sinon le score en direct prend le relais).
  const showPersisted = !answeredCount && prog && prog.answered > 0;

  return (
    <div className="bg-slate-800 border border-slate-700/50 rounded-2xl overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-3 px-4 md:px-5 py-4 text-left hover:bg-slate-700/30 transition-colors"
      >
        <LevelPill level={ex.level} />
        <span className="flex-1 font-semibold text-white truncate">{ex.title}</span>
        {registerLabel && (
          <span className="shrink-0 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-700 text-slate-300">
            {registerLabel}
          </span>
        )}
        {done && (
          <span className="shrink-0 text-xs font-bold text-emerald-400 tabular-nums">{score}/{total}</span>
        )}
        {showPersisted && (
          <span title={S.done} className="shrink-0 text-xs font-medium text-slate-400 tabular-nums">
            ✓ {prog.correct}/{prog.answered}
          </span>
        )}
        <svg viewBox="0 0 24 24" className={`shrink-0 w-4 h-4 fill-slate-500 transition-transform ${open ? 'rotate-180' : ''}`}>
          <path d="M7 10l5 5 5-5z" />
        </svg>
      </button>

      {open && (
        <div className="px-4 md:px-5 pb-6 flex flex-col gap-6">
          <div className="flex flex-col items-center gap-3 pt-1">
            <Speaker big onClick={() => play(audioSrc(ex))} label={S.listenAria} />
            <p className="text-xs text-slate-500 text-center">{S.listenPrompt}</p>
          </div>

          <div className="flex flex-col gap-6">
            {ex.questions.map((q, i) => (
              <Question
                key={i}
                question={q}
                tierLabel={S.tiers[q.tier] || q.tier}
                chosen={chosen[i] || null}
                onChoose={(opt) => onAnswer(ex.slug, i, opt, q.tier)}
              />
            ))}
          </div>

          <div className="flex flex-col gap-3 pt-1">
            <button
              onClick={() => setShowText((v) => !v)}
              className="self-start text-xs text-slate-400 hover:text-blue-300 underline underline-offset-4 transition-colors"
            >
              {showText ? S.hideText : S.showText}
            </button>
            {showText && (
              <p lang="ru" className="text-base md:text-lg text-blue-100 leading-relaxed" style={{ fontFamily: SERIF }}>
                {ex.text_ru}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default function ListeningComprehension({ play, soundEnabled, toggleSound, cursive, toggleCursive }) {
  const alang = 'fr';
  const S = STR[alang] || STR.fr;
  const [phase, setPhase] = useState('loading'); // loading | error | ready (sinon : redirection)
  const [raw, setRaw] = useState([]);
  const [filter, setFilter] = useState('all');
  const [openSlug, setOpenSlug] = useState(null);
  const [answers, setAnswers] = useState({}); // { slug: { qIndex: option } }
  const [progress, setProgress] = useState({}); // { slug: { answered, correct, last } } (persisté)
  const [token] = useState(() => {
    try { return localStorage.getItem(TOKEN_KEY); } catch { return null; }
  });

  useEffect(() => {
    // Page PERSONNELLE cachée : pour qui n'y a pas droit (non connecté = 401, ou
    // connecté sans être propriétaire = 403), on ne montre RIEN et on renvoie à
    // l'accueil — exactement comme si `?ecoute` n'existait pas. Surtout pas d'écran
    // « créez un compte » : il révélerait la section ET inviterait à s'inscrire pour
    // quelque chose d'inaccessible (le 403 tomberait juste après).
    // `replace` et non `assign` : pas d'entrée d'historique, le retour ne rebondit pas.
    const leave = () => { try { window.location.replace('/'); } catch {} };
    if (!token) { leave(); return; }
    const auth = { Authorization: `Bearer ${token}` };
    fetch('/api/listening', { headers: auth })
      .then((r) => {
        if (r.status === 401 || r.status === 403) { leave(); return null; }
        if (!r.ok) throw new Error('http');
        return r.json();
      })
      .then((d) => {
        if (d == null) return;
        setRaw(Array.isArray(d) ? d : []);
        setPhase('ready');
        // Progression persistée : marque les textes déjà travaillés (best effort).
        fetch('/api/listening/progress', { headers: auth })
          .then((r) => (r.ok ? r.json() : null))
          .then((p) => { if (p && typeof p === 'object') setProgress(p); })
          .catch(() => {});
      })
      .catch(() => setPhase('error'));
  }, [token]);

  const prepared = useMemo(() => prepare(raw, alang), [raw, alang]);
  const visible = useMemo(
    () => (filter === 'all' ? prepared : prepared.filter((e) => e.level === filter)),
    [prepared, filter],
  );

  const onAnswer = useCallback((slug, qIndex, opt, tier) => {
    // Déjà répondu à cette question ? on ne rejoue pas. On lit l'état COURANT
    // directement : le drapeau qu'on posait DANS l'updater setAnswers ne marchait
    // pas — React n'exécute l'updater qu'à la phase de rendu, donc APRÈS le test,
    // et le POST ne partait jamais (la table restait vide).
    if (answers[slug]?.[qIndex]) return;
    setAnswers((prev) => (
      prev[slug]?.[qIndex] ? prev : { ...prev, [slug]: { ...(prev[slug] || {}), [qIndex]: opt } }
    ));
    // Journalise la réponse en BDD (append-only) — base du suivi de progrès.
    // Fire-and-forget : l'exercice ne doit jamais dépendre du réseau.
    if (token) {
      fetch('/api/listening/answer', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ slug, tier, correct: !!opt.correct, chosen: opt.label }),
      }).catch(() => {});
    }
  }, [answers, token]);

  if (phase === 'loading') {
    return (
      <Shell actif="/?ecoute" cursive={cursive} toggleCursive={toggleCursive}>
        <div className="flex items-center justify-center py-24 text-slate-400">
          <div className="w-8 h-8 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
        </div>
      </Shell>
    );
  }

  if (phase === 'error') {
    return (
      <Shell actif="/?ecoute" cursive={cursive} toggleCursive={toggleCursive}>
        <div className="text-center flex flex-col items-center gap-4 pt-16">
          <p className="text-slate-400">{S.errorMsg}</p>
          <button onClick={() => window.location.reload()} className="px-6 py-2.5 bg-blue-600 hover:bg-blue-500 rounded-lg font-medium transition-colors">
            {S.retry}
          </button>
        </div>
      </Shell>
    );
  }

  return (
    <Shell actif="/?ecoute" cursive={cursive} toggleCursive={toggleCursive}>
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-2">
          <span className="text-2xl">🎧</span>
          <h1 className="text-xl md:text-2xl font-bold text-white">{S.title}</h1>
        </div>
        <p className="text-sm text-slate-400">{S.subtitle}</p>
      </div>

      {/* Filtre de niveau */}
      <div className="flex flex-wrap gap-2">
        {['all', ...LEVELS].map((lv) => {
          const active = filter === lv;
          return (
            <button
              key={lv}
              onClick={() => { setFilter(lv); setOpenSlug(null); }}
              className={`px-3.5 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                active
                  ? 'bg-blue-600 text-white border-blue-500'
                  : 'bg-slate-800 text-slate-300 border-slate-700 hover:bg-slate-700'
              }`}
            >
              {lv === 'all' ? S.all : lv}
            </button>
          );
        })}
      </div>

      <div className="flex flex-col gap-3">
        {visible.map((ex) => (
          <ExerciseCard
            key={ex.slug}
            ex={ex}
            S={S}
            open={openSlug === ex.slug}
            onToggle={() => setOpenSlug((s) => (s === ex.slug ? null : ex.slug))}
            play={play}
            answers={answers}
            onAnswer={onAnswer}
            prog={progress[ex.slug]}
          />
        ))}
        {!visible.length && (
          <p className="text-center text-slate-500 py-10">{S.empty}</p>
        )}
      </div>
    </Shell>
  );
}
